import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { api, apiErrorDetail } from "@/lib/api";
import { formatDateIt, romeToday, weekdayKey } from "@/lib/site";

const DAY_RANGE = /^\d{4}-\d{2}-\d{2}$/;

export default function AdminAgenda() {
  const { date: rawDate } = useParams();
  const date = DAY_RANGE.test(rawDate || "") ? rawDate : romeToday();
  const [auth, setAuth] = useState("checking");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(() => setAuth(true)).catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    if (auth !== true) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const site = (await api.get("/site")).data;
        const appts = (await api.get("/admin/appointments", { params: { from_date: date, to_date: date } })).data.appointments || [];
        const blocks = (await api.get("/admin/blocks")).data.blocks || [];
        if (cancelled) return;
        const settings = site.settings || {};
        const windows = ((settings.hours || {})[weekdayKey(date)]) || [];
        setData({
          contact: settings.contact || {},
          appts: appts
            .filter((a) => a.status === "pending" || a.status === "confirmed")
            .sort((a, b) => a.start.localeCompare(b.start)),
          windows,
          closed: (settings.blockedDates || []).includes(date),
          blocks: blocks.filter((b) => b.date === date),
        });
      } catch (e) {
        if (!cancelled) setError(apiErrorDetail(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, date]);

  if (auth === "checking") return <div className="min-h-screen bg-stone-200" data-testid="agenda-checking" />;

  if (auth === false) {
    return (
      <div className="min-h-screen bg-stone-200 flex items-center justify-center px-6" data-testid="agenda-denied">
        <div className="bg-white rounded-3xl p-10 text-center shadow-soft max-w-sm">
          <img src="/images/logo-transparent.png" alt="K Nutrition" className="h-12 mx-auto mb-5" />
          <p className="font-display text-3xl mb-2">Agenda del giorno</p>
          <p className="text-sm text-charcoal/60 mb-7">Accedi al pannello per vedere e stampare l'agenda.</p>
          <Link to="/admin" data-testid="agenda-goto-admin" className="inline-flex items-center gap-2 rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors">
            <ArrowLeft size={14} /> Vai al pannello
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-200 py-8 px-4" data-testid="agenda-page">
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          .agenda-noprint { display: none !important; }
          .agenda-sheet { box-shadow: none !important; border-radius: 0 !important; max-width: none !important; width: auto !important; margin: 0 !important; padding: 0 !important; min-height: auto !important; }
          body { background: #fff !important; }
        }
      `}</style>

      <div className="agenda-noprint max-w-[210mm] mx-auto flex items-center justify-between mb-5">
        <Link to="/admin" data-testid="agenda-back" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest2 font-medium text-charcoal/60 hover:text-deeptaupe transition-colors">
          <ArrowLeft size={14} /> Torna al pannello
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="admin-agenda-print-button"
          className="inline-flex items-center gap-2 rounded-full bg-deeptaupe text-ivory text-[12px] uppercase tracking-widest2 font-medium px-8 py-3 hover:bg-charcoal transition-colors"
        >
          <Printer size={15} /> Stampa
        </button>
      </div>

      <div className="agenda-sheet bg-white rounded-2xl shadow-soft max-w-[210mm] mx-auto px-10 py-12 min-h-[250mm]" data-testid="agenda-sheet">
        <div className="flex items-start justify-between gap-6 pb-6 border-b-2 border-charcoal/80">
          <div>
            <img src="/images/logo-transparent.png" alt="K Nutrition" className="h-12 mb-3" />
            <p className="text-[11px] uppercase tracking-widest2 text-taupe">Dott.ssa Francesca Notarfrancesco · Biologa Nutrizionista</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] uppercase tracking-widest2 text-taupe mb-1">Agenda del giorno</p>
            <p className="font-display text-3xl capitalize" data-testid="agenda-date">{formatDateIt(date)}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-1 py-4 text-[13px] text-charcoal/75">
          {data && data.closed && <span data-testid="agenda-closed">Studio chiuso in questa data.</span>}
          {data && !data.closed && (
            <span data-testid="agenda-hours">
              Orario dello studio: {data.windows.length ? data.windows.map((w) => `${w.start}–${w.end}`).join(" · ") : "non configurato"}
            </span>
          )}
          {data && data.blocks.map((b) => (
            <span key={b.mid} data-testid={`agenda-block-${b.mid}`}>
              Bloccato: {b.start ? `${b.start}–${b.end}` : "giornata intera"}{b.reason ? ` (${b.reason})` : ""}
            </span>
          ))}
        </div>

        {error && (
          <p className="my-8 rounded-xl bg-red-50 border border-red-200 text-red-700 px-5 py-3 text-sm" data-testid="agenda-error">
            {error}
          </p>
        )}

        {!data && !error ? (
          <p className="py-20 text-center text-charcoal/50 text-sm" data-testid="agenda-loading">Preparazione dell'agenda…</p>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-widest2 text-taupe font-medium mb-2">
              Appuntamenti ({data ? data.appts.length : 0})
            </p>
            {data && data.appts.length === 0 && (
              <p className="py-16 text-center text-charcoal/50 text-sm" data-testid="agenda-empty">
                Nessun appuntamento per questa giornata.
              </p>
            )}
            {data && data.appts.map((a) => (
              <div key={a.mid} className="flex gap-5 py-3.5 border-b border-stone-200 break-inside-avoid" data-testid={`agenda-row-${a.mid}`}>
                <span className="font-display text-2xl text-deeptaupe w-32 shrink-0">{a.start}–{a.end}</span>
                <span className="flex-1 min-w-0">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{a.name}</span>
                    {a.status === "pending" && (
                      <span className="text-[10px] uppercase tracking-widest2 font-medium bg-peach text-deeptaupe rounded-full px-2.5 py-0.5">
                        da confermare
                      </span>
                    )}
                  </span>
                  <span className="block text-[13px] text-charcoal/70 mt-0.5">
                    {a.service_name} · {a.phone || "—"}{a.email ? ` · ${a.email}` : ""}
                  </span>
                </span>
              </div>
            ))}
          </>
        )}

        <div className="mt-14 pt-4 border-t border-stone-200 flex flex-wrap justify-between gap-2 text-[11px] text-charcoal/50">
          <span>
            {data?.contact?.addressStreet || "—"}{data?.contact?.addressCity ? `, ${data.contact.addressCity}` : ""}{data?.contact?.phone ? ` · ${data.contact.phone}` : ""}
          </span>
          <span data-testid="agenda-printed-at">
            Stampata il {new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date())}
          </span>
        </div>
      </div>
    </div>
  );
}
